export const calculateDailyFinancials = (incomes, issuedLogs, filterStartDate, filterEndDate, forecastAutoCalc, forecastManualRate) => {
  const days = [];
  const startParts = filterStartDate.split('-').map(Number);
  const endParts = filterEndDate.split('-').map(Number);
  let currentDay = new Date(startParts[0], startParts[1] - 1, startParts[2]);
  const endDay = new Date(endParts[0], endParts[1] - 1, endParts[2]);
  
  while (currentDay <= endDay) {
    const yyyy = currentDay.getFullYear();
    const mm = String(currentDay.getMonth() + 1).padStart(2, '0');
    const dd = String(currentDay.getDate()).padStart(2, '0');
    days.push(`${yyyy}-${mm}-${dd}`);
    currentDay.setDate(currentDay.getDate() + 1);
  }

  const dailyFactMap = {};
  const actualWbIncomes = incomes.filter(i => (i.source === 'Wildberries' || !i.source));
  
  actualWbIncomes.forEach(i => {
    const startStr = i.period_start || i.date;
    const endStr = i.period_end || i.date;
    if (!startStr || !endStr) return;
    
    const startParts = startStr.split('-').map(Number);
    const endParts = endStr.split('-').map(Number);
    const startDate = new Date(startParts[0], startParts[1] - 1, startParts[2]);
    const endDate = new Date(endParts[0], endParts[1] - 1, endParts[2]);
    
    const daysInPeriod = [];
    let cur = new Date(startDate);
    while (cur <= endDate) {
      const yyyy = cur.getFullYear();
      const mm = String(cur.getMonth() + 1).padStart(2, '0');
      const dd = String(cur.getDate()).padStart(2, '0');
      daysInPeriod.push(`${yyyy}-${mm}-${dd}`);
      cur.setDate(cur.getDate() + 1);
    }
    
    const N = daysInPeriod.length;
    if (N === 0) return;
    
    const amountTotal = Number(i.amount||0) + Number(i.compensation||0) + Number(i.bonus||0) - Number(i.penalty||0);
    const turnoverTotal = Number(i.turnover || 0) > 0 ? Number(i.turnover) : amountTotal / 0.03;
    const saleByTariffTotal = Number(i.sale_by_tariff || 0) > 0 ? Number(i.sale_by_tariff) : turnoverTotal * 0.03;

    const periodIssuedLogs = issuedLogs.filter(l => l.work_date >= startStr && l.work_date <= endStr);
    const totalIssuedCount = periodIssuedLogs.reduce((sum, l) => sum + (Number(l.count) || 0), 0);
    
    daysInPeriod.forEach(d => {
      let ratio = 1 / N;
      if (totalIssuedCount > 0) {
        const dayLogs = periodIssuedLogs.filter(l => l.work_date === d);
        const dayCount = dayLogs.reduce((sum, l) => sum + (Number(l.count) || 0), 0);
        ratio = dayCount / totalIssuedCount;
      }
      
      if (!dailyFactMap[d]) {
        dailyFactMap[d] = { income: 0, turnover: 0, sale_by_tariff: 0 };
      }
      dailyFactMap[d].income += amountTotal * ratio;
      dailyFactMap[d].turnover += turnoverTotal * ratio;
      dailyFactMap[d].sale_by_tariff += saleByTariffTotal * ratio;
    });
  });

  let lastActualDate = null;
  if (actualWbIncomes.length > 0) {
    lastActualDate = actualWbIncomes.reduce((max, i) => {
      const val = i.period_end || i.date;
      return val > max ? val : max;
    }, actualWbIncomes[0].period_end || actualWbIncomes[0].date);
  }

  let averageDailyTurnover = 0;
  if (lastActualDate) {
    const lastDateObj = new Date(lastActualDate + 'T00:00:00');
    let count = 0;
    let sum = 0;
    for (let offset = 0; offset < 30; offset++) {
      const dObj = new Date(lastDateObj.getTime());
      dObj.setDate(dObj.getDate() - offset);
      const yyyy = dObj.getFullYear();
      const mm = String(dObj.getMonth() + 1).padStart(2, '0');
      const dd = String(dObj.getDate()).padStart(2, '0');
      const dateStr = `${yyyy}-${mm}-${dd}`;
      
      if (dailyFactMap[dateStr]) {
        sum += dailyFactMap[dateStr].turnover;
        count++;
      }
    }
    if (count > 0) {
      averageDailyTurnover = sum / count;
    }
  }

  if (averageDailyTurnover === 0) {
    const totalIssued = issuedLogs.reduce((acc, l) => acc + (l.count || 0), 0);
    const totalDays = issuedLogs.length || 1;
    averageDailyTurnover = (totalIssued / totalDays) * 2500;
  }
  if (averageDailyTurnover === 0) {
    averageDailyTurnover = 150000;
  }

  const getTurnoverForDate = (dateStr) => {
    if (dailyFactMap[dateStr]) {
      return dailyFactMap[dateStr].turnover;
    }
    if (lastActualDate && dateStr <= lastActualDate) {
      return 0;
    }
    const logMatches = issuedLogs.filter(l => l.work_date === dateStr);
    const dayCount = logMatches.reduce((sum, l) => sum + (Number(l.count) || 0), 0);
    if (dayCount > 0) {
      return dayCount * 2500;
    }
    return averageDailyTurnover;
  };

  const dailyData = {};
  days.forEach(d => {
    let income = 0;
    let turnover = 0;
    let tariffPercent = 0;
    let type = 'fact';

    if (lastActualDate && d <= lastActualDate) {
      type = 'fact';
      if (dailyFactMap[d]) {
        income = dailyFactMap[d].income;
        turnover = dailyFactMap[d].turnover;
        tariffPercent = turnover > 0 ? (dailyFactMap[d].sale_by_tariff / turnover) * 100 : 3.0;
      } else {
        income = 0;
        turnover = 0;
        tariffPercent = 3.0;
      }
    } else {
      type = 'forecast';
      if (forecastAutoCalc) {
        let rollingTurnover = 0;
        const dObj = new Date(d + 'T00:00:00');
        for (let i = 1; i <= 30; i++) {
          const tObj = new Date(dObj.getTime());
          tObj.setDate(tObj.getDate() - i);
          const yyyy = tObj.getFullYear();
          const mm = String(tObj.getMonth() + 1).padStart(2, '0');
          const dd = String(tObj.getDate()).padStart(2, '0');
          const tStr = `${yyyy}-${mm}-${dd}`;
          rollingTurnover += getTurnoverForDate(tStr);
        }
        tariffPercent = 3.885 - (rollingTurnover / 9000000);
        tariffPercent = Math.max(1.5, Math.min(4.5, tariffPercent));
      } else {
        tariffPercent = Number(forecastManualRate) || 3.0;
      }

      const logMatches = issuedLogs.filter(l => l.work_date === d);
      const dayCount = logMatches.reduce((sum, l) => sum + (Number(l.count) || 0), 0);
      if (dayCount > 0) {
        turnover = dayCount * 2500;
      } else {
        turnover = averageDailyTurnover;
      }
      income = turnover * (tariffPercent / 100);
    }

    dailyData[d] = {
      date: d,
      income,
      turnover,
      tariffPercent,
      type
    };
  });

  return { dailyData, days, lastActualDate, averageDailyTurnover, getTurnoverForDate };
}
